import { Box, Grid, LinearProgress } from "@mui/material";
import BaseContainer from "apps/WebCabApp/components/baseContainer";
import NotFound from "apps/WebCabApp/components/notFound";
import Sound, { skipNextDingSound } from "apps/WebCabApp/components/sound";
import useLayout from "apps/WebCabApp/layout/useLayout";
import CallOrderDialog from "apps/WebCabApp/providers/currentTripsProvider/callOrderDialog";
import { WSModel } from "apps/WebCabApp/ws";
import { WSMessage } from "apps/WebCabApp/ws/WSModel";
import React, { useEffect, useRef, useState } from "react";
import { useRouteMatch } from "react-router-dom";
import { AppTheme } from "theme";
import { orderSerializer } from "utils/order/orderSerializer";
import IOrder, { IPickUpConfig } from "utils/order/types";
import { requests } from "utils/requests";
import CallSign from "./callSign";
import MessageField from "./messageField";
import Messages from "./messages";
import { IMessage, IMessageStatus, MessageRole, messageSerializer } from "./type";
import SetPointOnMapDialog from "apps/WebCabApp/components/routePoints/setPointOnMapDialog";
import { useTranslation } from "react-i18next";
import { IPoint } from "utils/types";
import { uuidv7 } from "uuidv7";



export default function Chat() {
  const { t } = useTranslation();
  const match: any = useRouteMatch();
  const { setFixedHeight } = useLayout();
  const { playChatSent } = Sound();
  const lastMessagesRef = useRef(null);
  const [order, setOrder] = useState<IOrder | null>(null)
  const [pickUpConfig, setPickUpConfig] = useState<IPickUpConfig | null>(null)
  const [messages, setMessages] = useState<IMessage[] | null>(null)
  const [notFound, setNotFound] = useState(false);
  const [callOrder, setCallOrder] = useState<IOrder | null>(null);
  const [pointDialogOpen, setPointDialogOpen] = useState(false);
  const [infoPointDialog, setInfoPointDialog] = useState<IPoint|null>(null);
  const [messageUUID, setMessageUUID] = useState(uuidv7())


  useEffect(() => skipNextDingSound, [])

  useEffect(() => {
    requests.withoutErrorMessage().get(`/order/${match.params.id}/`, {
      acvtive: true,
    }).then((r) => {
      const _order = orderSerializer(r.body);
      setOrder(_order);
      setMessageUUID(uuidv7());
    }).catch((e) => {
      if (e.status === 404) {
        setNotFound(true);
      }
    })
  }, [match.params.id])

  useEffect(() => {
    if (order === null) return;
    requests.get(`/message/`, {
      order: order.id,
      limit: 0,
    }).then((r) => {
      setMessages(r.body.results.map((i: any) => messageSerializer(i)));
      setTimeout(() => {
        (lastMessagesRef.current as any)?.scrollIntoView({ behavior: 'auto' });
      }, 100)
    })
  }, [order])

  useEffect(() => {
    if (order === null) return;
    requests.get(`/order/${order.id}/pickup-config/`).then((r) => {
      setPickUpConfig(r.body);
    })
  }, [order])

  useEffect(() => {
    setFixedHeight(true)
    return () => {
      setFixedHeight(false)
    }
  }, [setFixedHeight])

  useEffect(() => {
    const WS = (message: WSMessage) => {
      const _message = messageSerializer(message.data)
      setMessages((o) => {
        if (o === null) return o;
        o = o.filter((m) => m.id !== _message.id);
        return [_message, ...o]
      })
      if (message.type === "webcab.order.message.new") {
        setTimeout(() => {
          (lastMessagesRef.current as any)?.scrollIntoView({ behavior: 'smooth' });
        }, 100)
      }
    }
    WSModel.subscribe(WS, /^webcab\.order\.message/);
    return () => {
      WSModel.unsubscribe(WS);
    }
  }, [])

  const onMessage = (message: string|null, point: IPoint|null) => {
    const newMessage: IMessage = {
      role: MessageRole.Client,
      message: message || t('point_message'),
      point: point,
      created_at: new Date(),
      statuses: [],
      local_id: Math.random().toString(36).substring(2, 15),
    }
    setMessages((prev) => [newMessage, ...prev!]);
    playChatSent();
    requests.post(`/message/`, {
      order: order?.id,
      message: message,
      point: point,
      message_uuid: messageUUID,
    }).then((r) => {
      setMessageUUID(uuidv7());
      const savedMessage = messageSerializer(r.body);
      setMessages((prev) => {
        const newMessages = prev!.map((m) => m.local_id === newMessage.local_id ? savedMessage : m);
        return newMessages;
      });
      setTimeout(() => {
        (lastMessagesRef.current as any)?.scrollIntoView({ behavior: 'auto' });
      }, 100)
    });
  };

  const onRead = (message: IMessage) => {
    requests.patch(`/message/${message.id}/read/`)
    const statuses: IMessageStatus[] = [
      ...message.statuses.filter((s) => s.role !== MessageRole.Client),
      {
        role: MessageRole.Client,
        read_at: new Date().getTime(),
      }
    ];
    const _message: IMessage = { ...message, statuses: statuses };
    setMessages((o) => {
      if (o === null) return o;
      o = o.filter((m) => m.id !== _message.id);
      return [_message, ...o]
    })
  }

  const onClick = (message: IMessage) => {
    if(!!message.point){
      setInfoPointDialog(message.point)
    }
  }

  if (notFound) {
    return (<NotFound />)
  }

  if (order === null || messages === null) {
    return (
      <BaseContainer>
        <LinearProgress />
      </BaseContainer>
    )
  }

  return (
    <React.Fragment>
      <Box sx={{
        flexGrow: 1,
        my: -4,
      }}>
        <Grid
          container
          direction={"column"}
          sx={{
            height: "100%",
            flexWrap: "nowrap",
          }}
        >
          <Grid item>
            {order.call_sign ? (
              <BaseContainer>
                <Box sx={{ my: 2 }}>
                  <CallSign
                    call_sign={order.call_sign}
                    onCall={() => {
                      setCallOrder(order)
                    }}
                    onClickPoint={() => {
                      setPointDialogOpen(true)
                    }}
                  />
                </Box>
              </BaseContainer>
            ) : null}
          </Grid>
          <Grid item sx={{
            flexGrow: 1,
            flexShrink: 1,
            overflow: "hidden",
          }}>
            <BaseContainer
              gridSx={{
                position: "relative",
                borderRadius: {
                  xs: 0,
                  sm: 6,
                },
                "&::after,&::before": {
                  content: "''",
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: 15,
                  zIndex: 2,
                  background: `linear-gradient(180deg,${AppTheme.lightBg} 10%, transparent 100%);`,
                  '@media (prefers-color-scheme: dark)': {
                    background: `linear-gradient(180deg,${AppTheme.lightBgDark} 10%, transparent 100%);`,
                  }
                },
                "&::before": {
                  top: "auto",
                  bottom: 0,
                  transform: "rotate(180deg)",
                }
              }}
              sx={{
                height: "100%",
                px: {
                  xs: 0,
                  sm: 3,
                },
                ">div": {
                  height: "100%",
                  ">div": {
                    height: "100%",
                    overflow: "hidden",
                  }
                }
              }}
            >
              <Box sx={{
                height: "100%",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                borderRadius: {
                  xs: 0,
                  sm: 6,
                },
                backgroundColor: AppTheme.lightBg,
                '@media (prefers-color-scheme: dark)': {
                  backgroundColor: AppTheme.lightBgDark,
                }
              }}>
                <Box
                  sx={{
                    overflowY: "auto",
                  }}
                >
                  <Messages
                    messages={messages}
                    onRead={onRead}
                    onClick={onClick}
                  />
                  <Box ref={lastMessagesRef} sx={{ height: 0 }}></Box>
                </Box>
              </Box>
            </BaseContainer>
          </Grid>
          <Grid item sx={{
            py: 2,
          }}>
            <BaseContainer>
              <MessageField onMessage={(message) => {
                onMessage(message, null)
              }} />
            </BaseContainer>
          </Grid>
        </Grid>
      </Box>
      <CallOrderDialog
        order={callOrder}
        onClose={() => {
          setCallOrder(null)
        }}
      />
      <SetPointOnMapDialog
        open={pointDialogOpen}
        title={t('send_location')}
        buttonText={t('send')}
        fixedAddress={order.routepoints[0]}
        area={{
          point: order.routepoints[0].point!,
          radius: pickUpConfig?.radius || 1000,
        }}
        onClose={() => {
          setPointDialogOpen(false);
        }}
        onSelect={(point) => {
          if(!!point?.point){
            onMessage(null, point.point)
          }
          setPointDialogOpen(false);
        }}
      />
      <SetPointOnMapDialog
        open={!!infoPointDialog}
        readOnly={true}
        title={t('point_message')}
        fixedAddress={order.routepoints[0]}
        markers={!!infoPointDialog ? ([
          {
            id: Math.random().toString(),
            style: "start_point",
            point: infoPointDialog,
          }
        ]) : undefined}
        center={infoPointDialog || undefined}
        onClose={() => {
          setInfoPointDialog(null);
        }}
      />
    </React.Fragment>
  );
}
